<script setup>
import { useDemoLoopStore } from '@/javascript/stores/demoLoop';

const demo = useDemoLoopStore();
</script>

<template>
  <Transition name="slide-up">
    <div
      v-if="demo.active"
      class="demo-narration-bar"
    >
      <div class="demo-narration-bar__text">
        {{ demo.narration }}
      </div>

      <div class="demo-narration-bar__controls">
        <!-- Progress dots -->
        <div class="demo-narration-bar__progress">
          <button
            v-for="i in demo.stepCount"
            :key="i"
            class="demo-progress-dot"
            :class="{
              'is-active': i - 1 === demo.currentStep,
              'is-done': i - 1 < demo.currentStep
            }"
            :aria-label="`Step ${i}`"
            @click="demo.jumpToStep(i - 1)"
          >
            <span
              v-if="i - 1 === demo.currentStep"
              class="demo-progress-dot__fill"
              :style="{ width: (demo.stepProgress * 100) + '%' }"
            />
          </button>
        </div>

        <span class="demo-narration-bar__counter">{{ demo.currentStep + 1 }}/{{ demo.stepCount }}</span>

        <button
          class="demo-narration-bar__btn"
          :aria-label="demo.paused ? 'Play' : 'Pause'"
          @click="demo.togglePause()"
        >
          <svg
            v-if="demo.paused"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
          ><path d="M8 5v14l11-7z" /></svg>
          <svg
            v-else
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
          ><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
        </button>

        <button
          class="demo-narration-bar__btn demo-narration-bar__btn--stop"
          aria-label="Stop"
          @click="demo.stop()"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="currentColor"
          ><path d="M18.3 5.71a1 1 0 00-1.42 0L12 10.59 7.12 5.71a1 1 0 00-1.42 1.42L10.59 12l-4.89 4.88a1 1 0 001.42 1.42L12 13.41l4.88 4.89a1 1 0 001.42-1.42L13.41 12l4.89-4.88a1 1 0 000-1.41z" /></svg>
        </button>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss">
.demo-narration-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 99995;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 24px;
  background: rgba(15, 23, 42, 0.92);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 0.9rem;
  pointer-events: auto;

  &__text {
    flex: 1;
    line-height: 1.4;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  &__progress {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  &__counter {
    font-size: 0.75rem;
    opacity: 0.7;
    white-space: nowrap;
  }

  &__btn {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: 1px solid rgba(255,255,255,0.2);
    background: rgba(255,255,255,0.08);
    color: #fff;
    cursor: pointer;
    transition: background 0.15s;

    &:hover { background: rgba(255,255,255,0.15); }
    &--stop:hover { background: rgba(239,68,68,0.3); }
  }
}

.demo-progress-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.25);
  cursor: pointer;
  padding: 0;
  transition: background 0.2s, transform 0.2s, width 0.3s, border-radius 0.3s;
  position: relative;
  overflow: hidden;

  &.is-done { background: #10be5a; }
  &.is-active {
    width: 24px;
    height: 8px;
    border-radius: 4px;
    background: rgba(255,255,255,0.15);
    transform: none;
    overflow: hidden;
  }
  &:hover { background: rgba(16, 190, 90, 0.7); }

  &__fill {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    background: #10be5a;
    border-radius: inherit;
  }
}

/* Slide transition */
.slide-up-enter-active, .slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-up-enter-from, .slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* Spotlight pulse animation */
@keyframes demo-pulse {
  0%, 100% { border-color: rgba(16, 190, 90, 0.8); box-shadow: 0 0 0 4000px rgba(0,0,0,0.25), 0 0 20px rgba(16, 190, 90, 0.5); }
  50% { border-color: rgba(16, 190, 90, 1); box-shadow: 0 0 0 4000px rgba(0,0,0,0.3), 0 0 30px rgba(16, 190, 90, 0.7); }
}

/* Click ripple animation */
@keyframes demo-click-ripple {
  0% { transform: scale(0.5); opacity: 1; }
  100% { transform: scale(2.5); opacity: 0; }
}

.demo-click-ripple {
  animation: demo-click-ripple 0.6s ease-out forwards;
}

/* Cursor styling */
.demo-cursor {
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
}
</style>
